import { useEffect, useId, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { consoleLegalUrl } from '@/lib/giving-terms';

interface GivingTermsDialogProps {
  open: boolean;
  onAccepted: () => void;
  onCancel: () => void;
}

export const GivingTermsDialog = ({ open, onAccepted, onCancel }: GivingTermsDialogProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const checkboxId = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [accepted, setAccepted] = useState(false);
  const [showRequired, setShowRequired] = useState(false);

  useEffect(() => {
    const node = dialogRef.current;
    if (!node) return;

    if (open) {
      setAccepted(false);
      setShowRequired(false);
      if (!node.open) node.showModal();
      return;
    }

    if (node.open) node.close();
  }, [open]);

  const termsUrl = consoleLegalUrl('terms');
  const privacyUrl = consoleLegalUrl('privacy');

  const submit = () => {
    if (!accepted) {
      setShowRequired(true);
      return;
    }
    onAccepted();
  };

  return (
    <dialog
      ref={dialogRef}
      className="give-dialog"
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onCancel();
      }}
    >
      <h2 id={titleId} className="give-dialog-title">
        {t('termsDialog.title')}
      </h2>
      <ul className="give-dialog-points">
        <li>{t('termsDialog.nonRefundable')}</li>
        <li>{t('termsDialog.contactChurch')}</li>
      </ul>
      <div className="give-check">
        <input
          id={checkboxId}
          type="checkbox"
          checked={accepted}
          onChange={(event) => {
            setAccepted(event.target.checked);
            if (event.target.checked) setShowRequired(false);
          }}
        />
        <label htmlFor={checkboxId} className="give-check-label">
          <Trans
            i18nKey="termsDialog.accept"
            components={{
              terms: (
                <a
                  href={termsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('termsDialog.termsAria')}
                />
              ),
              privacy: (
                <a
                  href={privacyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('termsDialog.privacyAria')}
                />
              ),
            }}
          />
        </label>
      </div>
      {showRequired ? (
        <p className="give-message give-message--error" role="alert">
          {t('termsDialog.required')}
        </p>
      ) : null}
      <div className="give-dialog-actions">
        <button type="button" className="ghost" onClick={onCancel}>
          {t('termsDialog.cancel')}
        </button>
        <button type="button" className="primary" onClick={submit} disabled={!accepted}>
          {t('termsDialog.continue')}
        </button>
      </div>
    </dialog>
  );
};
